import { fileToDataUrl } from '@/lib/userAvatar';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import { useAuth } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';
import MaterialIcon from '@/components/MaterialIcon';
import { GitHub } from '@/components/icons/Github';
import { GoogleDrive } from '@/components/icons/GoogleDrive';
import UserAvatar, { useUserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import {
  type ConnectorId,
  useConnectorPreferences
} from '@/hooks/useConnectorPreferences';

import { profileState } from '@/state/user';

import Page from './Page';

const ISIT_MODULI = [
  { key: 'it', path: 'wizard.form.modul.isit.it' },
  { key: 'is', path: 'wizard.form.modul.isit.is' },
  { key: 'ie', path: 'wizard.form.modul.isit.ie' },
  { key: 'ba', path: 'wizard.form.modul.isit.ba' },
  { key: 'se', path: 'wizard.form.modul.isit.se' },
  { key: 'eb', path: 'wizard.form.modul.isit.eb' }
];

const MENADZMENT_MODULI = [
  { key: 'fm', path: 'wizard.form.modul.menadzment.fm' },
  { key: 'lo', path: 'wizard.form.modul.menadzment.lo' },
  { key: 'mk', path: 'wizard.form.modul.menadzment.mk' },
  { key: 'qs', path: 'wizard.form.modul.menadzment.qs' },
  { key: 'om', path: 'wizard.form.modul.menadzment.om' },
  { key: 'pm', path: 'wizard.form.modul.menadzment.pm' }
];

type Section = 'account' | 'connectors';

interface SettingsFormProps {
  inModal?: boolean;
  onClose?: () => void;
}

export function SettingsForm({
  inModal: _inModal,
  onClose
}: SettingsFormProps) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const setProfile = useSetRecoilState(profileState);
  const avatar = useUserAvatar(user?.identifier, user?.metadata?.image);
  const { enabledConnectors, setConnectorEnabled } = useConnectorPreferences();

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [section, setSection] = useState<Section>('account');

  const [displayName, setDisplayName] = useState('');
  const [year, setYear] = useState('');
  const [smer, setSmer] = useState('');
  const [modul, setModul] = useState('');
  const [customModul, setCustomModul] = useState('');
  const [akreditacija, setAkreditacija] = useState('');
  const [godinaStudija, setGodinaStudija] = useState('');

  const [originalValues, setOriginalValues] = useState({
    displayName: '',
    year: '',
    smer: '',
    modul: '',
    customModul: '',
    akreditacija: '',
    godinaStudija: ''
  });

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user?.identifier) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/profile/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.identifier })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            t('settings.errors.loadFailed', 'Failed to load profile')
        );
      }

      const profile = data.profile;
      setProfile(profile);
      const parsedDisplayName = profile.display_name || '';
      const parsedGodinaStudija = profile.godina_studija || '';

      const yearText = (profile.year || '').toLowerCase();
      let parsedYear = '';
      if (
        yearText.includes('oas') ||
        yearText.includes('undergraduate') ||
        yearText.includes('osnovne')
      ) {
        parsedYear = 'OAS';
      } else if (yearText.includes('mas') || yearText.includes('master')) {
        parsedYear = 'MAS';
      } else if (
        yearText.includes('sas') ||
        yearText.includes('specialist') ||
        yearText.includes('specijalističke')
      ) {
        parsedYear = 'SAS';
      }

      const akredText = (profile.akreditacija || '').toLowerCase();
      let parsedAkreditacija = '';
      if (
        akredText.includes('2023') ||
        akredText.includes('nova') ||
        akredText.includes('new')
      ) {
        parsedAkreditacija = 'nova';
      } else if (
        akredText.includes('2014') ||
        akredText.includes('stara') ||
        akredText.includes('old')
      ) {
        parsedAkreditacija = 'stara';
      }

      const smerValue = (profile.smer || '').toLowerCase();
      let parsedSmer = '';
      if (
        smerValue.includes('isit') ||
        smerValue.includes('information') ||
        smerValue.includes('informacioni')
      ) {
        parsedSmer = 'ISIT';
      } else if (
        smerValue.includes('menadzment') ||
        smerValue.includes('management') ||
        smerValue.includes('menadžment')
      ) {
        parsedSmer = 'Menadzment';
      }

      const modulValue = profile.modul || '';
      let parsedModul = '';
      let parsedCustomModul = '';
      if (modulValue) {
        const allModuli = [...ISIT_MODULI, ...MENADZMENT_MODULI];
        const foundModul = allModuli.find(
          (m) => t(m.path, m.key) === modulValue
        );
        if (foundModul) {
          parsedModul = foundModul.key;
        } else {
          parsedCustomModul = modulValue;
        }
      }

      setDisplayName(parsedDisplayName);
      setGodinaStudija(parsedGodinaStudija);
      setYear(parsedYear);
      setAkreditacija(parsedAkreditacija);
      setSmer(parsedSmer);
      setModul(parsedModul);
      setCustomModul(parsedCustomModul);

      setOriginalValues({
        displayName: parsedDisplayName,
        year: parsedYear,
        smer: parsedSmer,
        modul: parsedModul,
        customModul: parsedCustomModul,
        akreditacija: parsedAkreditacija,
        godinaStudija: parsedGodinaStudija
      });
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast.error(
        error.message ||
          t('settings.errors.loadFailed', 'Failed to load profile')
      );
    } finally {
      setLoading(false);
    }
  };

  const getModulOptions = () => {
    if (smer === 'ISIT') return ISIT_MODULI;
    if (smer === 'Menadzment') return MENADZMENT_MODULI;
    return [];
  };

  const getGodinaStudijaOptions = () => {
    if (year === 'OAS') return ['1', '2', '3', '4'];
    if (year === 'MAS' || year === 'SAS') return ['1', '2'];
    return [];
  };

  const showModulSelect =
    year === 'OAS' &&
    akreditacija === 'nova' &&
    (smer === 'ISIT' || smer === 'Menadzment');
  const showCustomModul =
    ((year === 'MAS' || year === 'SAS') && smer) ||
    (year === 'OAS' && akreditacija === 'stara' && smer);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateData: Record<string, string> = {};

    if (displayName !== originalValues.displayName) {
      updateData.display_name = displayName;
    }

    if (year !== originalValues.year) {
      updateData.year = year
        ? t(`wizard.form.year.options.${year.toLowerCase()}`, year)
        : '';
    }

    if (smer !== originalValues.smer) {
      updateData.smer = smer
        ? t(`wizard.form.smer.options.${smer.toLowerCase()}`, smer)
        : '';
    }

    if (akreditacija !== originalValues.akreditacija) {
      updateData.akreditacija = akreditacija
        ? t(`wizard.form.akreditacija.options.${akreditacija}`, akreditacija)
        : '';
    }

    if (godinaStudija !== originalValues.godinaStudija) {
      updateData.godina_studija = godinaStudija;
    }

    const currentModulValue = modul || customModul;
    const originalModulValue =
      originalValues.modul || originalValues.customModul;

    if (currentModulValue !== originalModulValue) {
      let modulText = '';
      if (year === 'OAS' && akreditacija === 'nova' && modul) {
        const modulOption = getModulOptions().find((m) => m.key === modul);
        modulText = modulOption ? t(modulOption.path, modul) : '';
      } else if (customModul) {
        modulText = customModul;
      }
      updateData.modul = modulText;
    }

    if (Object.keys(updateData).length === 0) {
      toast.info(t('settings.errors.noChanges', 'No changes to save'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/profile/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            t('settings.errors.updateFailed', 'Failed to update profile')
        );
      }

      toast.success(
        t('settings.success.updated', 'Profile updated successfully')
      );
      setProfile(data.profile);
      window.dispatchEvent(new Event('profile-updated'));
      await loadProfile();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(
        error.message ||
          t('settings.errors.updateFailed', 'Failed to update profile')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.identifier) return;

    try {
      avatar.saveImage(await fileToDataUrl(file));
      toast.success(t('settings.avatar.updated', 'Profile photo updated'));
    } catch (error) {
      const key =
        error instanceof Error && error.message === 'file-too-large'
          ? 'settings.avatar.tooLarge'
          : 'settings.avatar.invalid';
      toast.error(
        t(
          key,
          error instanceof Error && error.message === 'file-too-large'
            ? 'The image must be smaller than 1 MB.'
            : 'Choose a PNG, JPG, or WebP image.'
        )
      );
    }
  };

  const handleSectionClick = (s: Section) => setSection(s);

  const activeSection = section;

  const personalItems: { icon: string; label: string; section: Section }[] = [
    {
      icon: 'manage_accounts',
      label: t('settings.sections.account', 'Account'),
      section: 'account'
    }
  ];

  const connectorItems: { icon: string; label: string; section: Section }[] = [
    {
      icon: 'layers',
      label: t('settings.sections.connectors', 'Connectors'),
      section: 'connectors'
    }
  ];

  const sectionTitle: Record<Section, string> = {
    account: t('settings.sections.account', 'Account'),
    connectors: t('settings.sections.connectors', 'Connectors')
  };

  const connectorOptions: {
    id: ConnectorId;
    icon: string;
    name: string;
    description: string;
  }[] = i18n.language.toLowerCase().startsWith('en')
    ? [
        {
          id: 'googleDrive',
          icon: 'layers',
          name: 'Google Drive',
          description: 'Add files from Google Drive to your chats.'
        },
        // Waiting for approval from FON.
        // {
        //   id: 'microsoft',
        //   icon: 'computer',
        //   name: 'Microsoft',
        //   description: 'Add files from OneDrive or Teams to your chats.'
        // },
        {
          id: 'github',
          icon: 'work',
          name: 'GitHub',
          description: 'Add repositories to your chats.'
        }
      ]
    : [
        {
          id: 'googleDrive',
          icon: 'layers',
          name: 'Google Drive',
          description: 'Dodajte fajlove sa Google Drive-a u razgovore.'
        },
        // Waiting for approval from FON.
        // {
        //   id: 'microsoft',
        //   icon: 'computer',
        //   name: 'Microsoft',
        //   description: 'Dodajte fajlove sa OneDrive-a ili Teams-a u razgovore.'
        // },
        {
          id: 'github',
          icon: 'work',
          name: 'GitHub',
          description: 'Dodajte repozitorijume u razgovore.'
        }
      ];

  const AccountSection = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center w-full h-48">
          <Loader />
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-8 pt-2">
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mb-2 text-center sm:text-left">
          <div className="relative flex-shrink-0">
            <UserAvatar
              identifier={user?.identifier}
              displayName={displayName}
              providerImage={user?.metadata?.image}
              className="h-20 w-20 border-2 shadow-lg"
              fallbackClassName="text-2xl"
            />
            <label
              htmlFor="profile-avatar"
              className="fongpt-hover absolute -bottom-1 -right-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-outline bg-surface text-secondary shadow-md"
              title={t('settings.avatar.change', 'Change profile photo')}
            >
              <MaterialIcon name="photo_camera" className="text-[17px]" />
            </label>
            <input
              id="profile-avatar"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={handleAvatarChange}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-headline-md text-headline-md text-primary truncate">
              {displayName || user?.identifier || 'User'}
            </h3>
            <p className="font-body-sm text-body-sm text-secondary truncate">
              {user?.identifier || ''}
            </p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <label
                htmlFor="profile-avatar"
                className="fongpt-hover-text cursor-pointer font-medium text-secondary"
              >
                {t('settings.avatar.change', 'Change profile photo')}
              </label>
              {avatar.hasCustomImage ? (
                <button
                  type="button"
                  onClick={avatar.removeImage}
                  className="fongpt-hover-text font-medium text-secondary"
                >
                  {t('settings.avatar.remove', 'Remove photo')}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label
              htmlFor="profile-name"
              className="font-label-sm text-label-sm text-muted uppercase tracking-wider block"
            >
              {t('wizard.form.name.label', 'Name')}
            </Label>
            <Input
              id="profile-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md focus:outline-none focus:border-outline-variant transition-colors placeholder:text-primary/30"
              placeholder={t('wizard.form.name.placeholder', 'Enter your name')}
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="profile-email"
              className="font-label-sm text-label-sm text-muted uppercase tracking-wider block"
            >
              {t('settings.emailLabel', 'Email')}
            </Label>
            <Input
              id="profile-email"
              type="email"
              value={user?.identifier || ''}
              readOnly
              className="w-full px-4 py-3 rounded-xl bg-surface/20 border border-outline text-muted font-body-md cursor-not-allowed"
            />
            <p className="text-[11px] text-secondary font-body-sm">
              {t('settings.emailLocked', 'Email cannot be changed.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                {t('wizard.form.year.label', 'Study Level')}
              </Label>
              <Select
                value={year}
                onValueChange={(val) => {
                  setYear(val);
                  setGodinaStudija('');
                }}
              >
                <SelectTrigger className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md">
                  <SelectValue
                    placeholder={t(
                      'wizard.form.year.placeholder',
                      'Select year'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OAS">
                    {t(
                      'wizard.form.year.options.oas',
                      'Undergraduate Studies (OAS)'
                    )}
                  </SelectItem>
                  <SelectItem value="MAS">
                    {t('wizard.form.year.options.mas', 'Master Studies (MAS)')}
                  </SelectItem>
                  <SelectItem value="SAS">
                    {t(
                      'wizard.form.year.options.sas',
                      'Specialist Studies (SAS)'
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {year && (
              <div className="space-y-1.5">
                <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                  {t('wizard.form.godinaStudija.label', 'Year')}
                </Label>
                <Select value={godinaStudija} onValueChange={setGodinaStudija}>
                  <SelectTrigger className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md">
                    <SelectValue
                      placeholder={t(
                        'wizard.form.godinaStudija.placeholder',
                        'Select year'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getGodinaStudijaOptions().map((god) => (
                      <SelectItem key={god} value={god}>
                        {god}. {t('wizard.form.godinaStudija.godina', 'year')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                {t('wizard.form.akreditacija.label', 'Accreditation')}
              </Label>
              <Select value={akreditacija} onValueChange={setAkreditacija}>
                <SelectTrigger className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md">
                  <SelectValue
                    placeholder={t(
                      'wizard.form.akreditacija.placeholder',
                      'Select accreditation'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nova">
                    {t('wizard.form.akreditacija.options.nova')}
                  </SelectItem>
                  <SelectItem value="stara">
                    {t('wizard.form.akreditacija.options.stara')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                {t('wizard.form.smer.label', 'Program')}
              </Label>
              <Select
                value={smer}
                onValueChange={(val) => {
                  setSmer(val);
                  setModul('');
                  setCustomModul('');
                }}
              >
                <SelectTrigger className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md">
                  <SelectValue
                    placeholder={t(
                      'wizard.form.smer.placeholder',
                      'Select program'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ISIT">
                    {t(
                      'wizard.form.smer.options.isit',
                      'Information Systems and Technologies'
                    )}
                  </SelectItem>
                  <SelectItem value="Menadzment">
                    {t('wizard.form.smer.options.menadzment', 'Management')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showModulSelect && (
              <div className="space-y-1.5">
                <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                  {t('wizard.form.modul.labelOptional', 'Module')}
                </Label>
                <Select value={modul} onValueChange={setModul}>
                  <SelectTrigger className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md">
                    <SelectValue
                      placeholder={t(
                        'wizard.form.modul.placeholder',
                        'Select module'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {getModulOptions().map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {t(option.path, option.key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showCustomModul && (
              <div className="space-y-1.5">
                <Label className="font-label-sm text-label-sm text-muted uppercase tracking-wider block">
                  {t('wizard.form.modul.labelOptional', 'Module')}
                </Label>
                <Input
                  value={customModul}
                  onChange={(e) => setCustomModul(e.target.value)}
                  placeholder={t(
                    'wizard.form.modul.placeholderCustom',
                    'Enter your module'
                  )}
                  className="w-full px-4 py-3 rounded-xl bg-surface/40 border border-outline text-primary font-body-md focus:outline-none focus:border-outline-variant transition-colors placeholder:text-primary/30"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 pt-2">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-primary text-background rounded-xl font-label-md font-semibold hover:bg-primary-hover active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <MaterialIcon
                  name="progress_activity"
                  className="animate-spin"
                />
                {t('settings.actions.saving', 'Saving...')}
              </>
            ) : (
              t('settings.actions.save', 'Save Changes')
            )}
          </Button>
        </div>
      </form>
    );
  };

  const ConnectorGlyph = ({ id }: { id: ConnectorId }) => {
    if (id === 'googleDrive') return <GoogleDrive className="size-5" />;
    if (id === 'github')
      return <GitHub className="size-5 fill-current text-primary" />;
    return <MaterialIcon name="link" className="text-[20px]" />;
  };

  const ConnectorsSection = () => (
    <div className="space-y-6 pt-2">
      <div>
        <h3 className="font-headline-sm text-headline-sm text-primary">
          {i18n.language.toLowerCase().startsWith('en')
            ? 'Choose your integrations'
            : 'Izaberite integracije'}
        </h3>
        <p className="mt-1 text-sm text-secondary">
          {i18n.language.toLowerCase().startsWith('en')
            ? 'Enabled integrations appear in the attachments menu in chat.'
            : 'Uključene integracije se prikazuju u meniju za priloge u razgovoru.'}
        </p>
      </div>

      <div className="space-y-3">
        {connectorOptions.map((connector) => (
          <div
            key={connector.id}
            className="flex items-center justify-between gap-4 rounded-xl border border-outline bg-surface/30 px-4 py-3.5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ConnectorGlyph id={connector.id} />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-primary">{connector.name}</p>
                <p className="mt-0.5 text-sm text-secondary">
                  {connector.description}
                </p>
              </div>
            </div>
            <Switch
              checked={enabledConnectors[connector.id]}
              onCheckedChange={(enabled) =>
                setConnectorEnabled(connector.id, enabled)
              }
              aria-label={`${connector.name} ${
                i18n.language.toLowerCase().startsWith('en')
                  ? 'connector'
                  : 'konektor'
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'account':
        return AccountSection();
      case 'connectors':
        return ConnectorsSection();
    }
  };

  const NavButton = ({
    item
  }: {
    item: { icon: string; label: string; section: Section };
  }) => (
    <button
      onClick={() => handleSectionClick(item.section)}
      className={`fongpt-hover w-full flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-xl transition-colors text-left whitespace-nowrap cursor-pointer ${
        section === item.section
          ? 'is-active md:bg-[#c4b5fd]/20 md:text-primary'
          : 'text-secondary'
      }`}
    >
      <MaterialIcon name={item.icon} />
      <span className="text-sm font-medium">{item.label}</span>
      <span className="ml-auto text-secondary md:hidden">
        <MaterialIcon name="chevron_right" />
      </span>
    </button>
  );

  return (
    <>
      <aside className="settings-sidebar w-full md:w-[260px] flex flex-col border-b md:border-b-0 md:border-r border-white/[0.06] p-4">
        <div className="hidden md:flex items-center gap-2 mb-4 px-1">
          <h2 className="text-lg font-semibold text-primary">
            {t('navigation.user.menu.settings', 'Settings')}
          </h2>
        </div>

        <nav className="flex-1 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto pb-2 md:pb-0">
          <div className="flex md:flex-col gap-2">
            <p className="text-[10px] font-semibold text-muted px-2 pb-1 tracking-[0.14em] hidden md:block">
              {t('settings.groups.profile', 'PROFILE')}
            </p>
            {personalItems.map((item) => (
              <NavButton key={item.section} item={item} />
            ))}
          </div>
          <div className="flex md:flex-col gap-2">
            <p className="text-[10px] font-semibold text-muted px-2 pb-1 tracking-[0.14em] hidden md:block">
              {i18n.language.toLowerCase().startsWith('en')
                ? 'INTEGRATIONS'
                : 'INTEGRACIJE'}
            </p>
            {connectorItems.map((item) => (
              <NavButton key={item.section} item={item} />
            ))}
          </div>
        </nav>
      </aside>

      <section className="flex-1 flex flex-col h-full overflow-y-auto bg-transparent relative">
        <header className="sticky top-0 z-10 px-6 md:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="font-headline-md text-headline-md text-primary">
              {sectionTitle[activeSection]}
            </h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="fongpt-hover p-2 rounded-full text-secondary"
            >
              <MaterialIcon
                name="close"
              />
            </button>
          )}
        </header>

        <div className="settings-content px-4 md:px-10 py-6 md:py-9 max-w-3xl mx-auto w-full">
          {renderSection()}
        </div>
      </section>

    </>
  );
}

export default function Settings() {
  return (
    <Page>
      <div className="w-full h-full flex flex-col md:flex-row relative animate-settings-in">
        <SettingsForm />
      </div>
    </Page>
  );
}
